import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BedDouble, Building2, IndianRupee, Plus, ShieldCheck, Trash2, Clock, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Stays() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [stays, setStays] = useState([]);
  const [form, setForm] = useState({ name: "", city: "", locality: "", address: "", contact_phone: "", default_rent: "", rent_due_day: "5" });
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/stay/access");
      setAccess(data);
      if (data.granted) {
        const res = await api.get("/stays", { params: { kind: "pg" } });
        setStays(res.data);
      }
    } catch (err) {
      toast.error(errText(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const requestAccess = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stay/access-request", null, { params: { scope: "stay" } });
      toast.success(data.status === "granted" ? "Access already granted" : "Request sent to the admin");
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const createStay = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.city.trim()) {
      toast.error("Name and city are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        kind: "pg",
        default_rent: Number(form.default_rent || 0),
        rent_due_day: Number(form.rent_due_day || 5),
      };
      if (editing) await api.put(`/stays/${editing}`, payload);
      else await api.post("/stays", payload);
      toast.success(editing ? "PG details updated" : "PG / stay added");
      resetForm();
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setForm({ name: "", city: "", locality: "", address: "", contact_phone: "", default_rent: "", rent_due_day: "5" });
    setEditing(null);
    setCreating(false);
  };

  const startEdit = (s) => {
    setForm({
      name: s.name || "", city: s.city || "", locality: s.locality || "", address: s.address || "",
      contact_phone: s.contact_phone || "", default_rent: String(s.default_rent ?? ""),
      rent_due_day: String(s.rent_due_day ?? 5),
    });
    setEditing(s.stay_id);
    setCreating(true);
    setTimeout(() => document.getElementById("stay-form")?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  const removeStay = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? All its floors, rooms, beds and tenant records will be removed.`)) return;
    try {
      await api.delete(`/stays/${id}`);
      toast.success("Stay removed");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  if (!access)
    return (
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );

  if (!access.granted)
    return (
      <div className="mx-auto max-w-[720px] px-5 md:px-10 py-16" data-testid="stay-access-gate">
        <div className="rounded-2xl border border-border p-8">
          <ShieldCheck className="h-7 w-7 text-primary" strokeWidth={1.8} />
          <h1 className="mt-5 font-display text-2xl">Stay / PG management</h1>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Manage floors, rooms, beds and tenant rent for your PG or co-living property. Access is granted by
            the Houses7.com admin — request it once and you'll see your dashboard here.
          </p>
          {access.status === "pending" ? (
            <p className="mt-6 flex items-center gap-2 rounded-xl bg-muted px-4 py-3 text-sm" data-testid="stay-request-pending">
              <Clock className="h-4 w-4 text-primary" /> Request pending — the admin will approve it shortly.
            </p>
          ) : (
            <Button
              onClick={requestAccess}
              disabled={busy}
              className="mt-6 h-12 rounded-lg font-bold hover:brightness-95"
              data-testid="stay-request-access-btn"
            >
              {busy ? "Sending…" : "Request access"}
            </Button>
          )}
          {access.status === "revoked" && (
            <p className="mt-4 text-[13px] text-destructive" data-testid="stay-access-revoked">
              Your access was revoked. Request again if you need it back.
            </p>
          )}
        </div>
      </div>
    );

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Stay / PG management</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {user?.role === "admin" ? "All PGs on Houses7.com" : "Your PGs and co-living properties"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/stays/collections">
            <Button
              variant="outline"
              className="h-12 rounded-lg font-bold"
              data-testid="stay-collections-link"
            >
              <IndianRupee className="h-4 w-4 mr-2" /> Collections board
            </Button>
          </Link>
          <Button
            className="h-12 rounded-lg font-bold hover:brightness-95"
            onClick={() => (creating ? resetForm() : setCreating(true))}
            data-testid="stay-add-toggle-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add a PG / stay
          </Button>
        </div>
      </div>

      {creating && (
        <form onSubmit={createStay} id="stay-form" className="mt-7 rounded-2xl border border-border p-6 md:p-8" data-testid="stay-create-form">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-display text-lg">{editing ? "Edit PG details" : "New PG / stay"}</h2>
            {editing && (
              <button type="button" onClick={resetForm} className="text-[12px] font-semibold underline" data-testid="stay-cancel-edit">
                Cancel
              </button>
            )}
          </div>
          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              ["name", "PG name"],
              ["city", "City"],
              ["locality", "Locality"],
              ["address", "Address"],
              ["contact_phone", "Contact phone"],
              ["default_rent", "Default rent per bed ₹"],
              ["rent_due_day", "Rent due day of month (1-28)"],
            ].map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs font-semibold">{label}</Label>
                <Input
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="h-12 rounded-lg"
                  data-testid={`stay-${k.replace(/_/g, "-")}-input`}
                />
              </div>
            ))}
          </div>
          <Button type="submit" disabled={busy} className="mt-6 h-12 rounded-lg font-bold hover:brightness-95" data-testid="stay-create-btn">
            {busy ? "Saving…" : editing ? "Save changes" : "Create stay"}
          </Button>
        </form>
      )}

      {stays.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-border p-10" data-testid="stays-empty">
          <Building2 className="h-7 w-7 text-primary" strokeWidth={1.8} />
          <p className="mt-4 font-display text-xl">No PG added yet</p>
          <p className="mt-2 text-sm text-muted-foreground">Add your PG, then create floors, rooms and beds inside it.</p>
        </div>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="stays-grid">
          {stays.map((s) => (
            <div key={s.stay_id} className="rounded-2xl border border-border p-6" data-testid={`stay-card-${s.stay_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Link to={`/stays/${s.stay_id}`} className="font-display text-lg hover:underline">
                    {s.name}
                  </Link>
                  <p className="mt-1 text-[13px] text-muted-foreground">
                    {[s.locality, s.city].filter(Boolean).join(", ")}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-lg"
                    onClick={() => startEdit(s)}
                    data-testid={`stay-edit-${s.stay_id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-lg text-destructive"
                    onClick={() => removeStay(s.stay_id, s.name)}
                    data-testid={`stay-delete-${s.stay_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2 text-[12px] font-semibold">
                <span className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5">
                  <BedDouble className="h-3.5 w-3.5" /> {s.total_beds} beds
                </span>
                <span className="rounded-full bg-muted px-3 py-1.5">{s.occupied_beds} occupied</span>
                <span
                  className={`rounded-full px-3 py-1.5 ${
                    s.unpaid_beds ? "bg-destructive/10 text-destructive" : "bg-emerald-500/10 text-emerald-700"
                  }`}
                  data-testid={`stay-unpaid-${s.stay_id}`}
                >
                  {s.unpaid_beds ? `${s.unpaid_beds} rent due` : "All rent paid"}
                </span>
              </div>

              <Link to={`/stays/${s.stay_id}`}>
                <Button variant="outline" className="mt-5 h-11 w-full rounded-lg" data-testid={`stay-manage-${s.stay_id}`}>
                  Manage beds & tenants
                </Button>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
